import {
  DEFAULT_ACCOUNT_HEALTH_THRESHOLDS,
  normalizeAccountHealthThresholds,
  type AccountHealthThresholds,
  type BusinessHealthThresholds,
} from "../../../../../Utils/HealthThresholds";
import {
  BUSINESS_HEALTH_COLORS,
  HEALTH_DOT_COLORS,
  BH_STABLE,
  BH_STRONG,
  BH_CONCERN,
  businessHealthBand,
  businessHealthClass,
  businessHealthColor,
  businessHealthLabel,
} from "../../../../../Utils/Helper/businessHealth";

export type BirdsEyeClusterId =
  | "renewal-risk"
  | "strong-performers"
  | "high-adoption-mixed"
  | "kpi-data-not-available";

export type BirdsEyeMapClusterId = Exclude<
  BirdsEyeClusterId,
  "kpi-data-not-available"
>;

/** Opaque bubble fills (match CLUSTER_META.bg in BirdsEyeOverview). */
export const BIRDS_EYE_NODE_FILL: Record<BirdsEyeMapClusterId, string> = {
  "renewal-risk": "#fee2e2",
  "strong-performers": "#dcfce7",
  "high-adoption-mixed": "#fefce8",
};

export const BIRDS_EYE_NODE_FILL_FALLBACK = "#f3f4f6";

export const clampOverallScore = (score: number): number =>
  Math.max(0, Math.min(100, Number.isFinite(score) ? score : 0));

export {
  BUSINESS_HEALTH_COLORS,
  HEALTH_DOT_COLORS,
  BH_STABLE,
  BH_STRONG,
  BH_CONCERN,
  businessHealthBand,
  businessHealthClass,
  businessHealthColor,
  businessHealthLabel,
};

/** Cluster bands from Business Health Settings (Concern / Stable / Strong). */
export const clusterFromOverall = (
  overall: number,
  thresholds?: Partial<BusinessHealthThresholds> | null
): BirdsEyeMapClusterId => {
  const score = clampOverallScore(overall);
  const band = businessHealthClass(score, thresholds);
  if (band === "Strong") return "strong-performers";
  if (band === "Stable") return "high-adoption-mixed";
  return "renewal-risk";
};

/**
 * Health dot on map bubbles — Business Health bands from Settings thresholds.
 */
export const healthDotColorFromOverall = (
  overall: number,
  thresholds?: Partial<BusinessHealthThresholds> | null
): string => {
  return businessHealthColor(overall, thresholds) ?? HEALTH_DOT_COLORS.red;
};

export const nodeFillFromOverall = (
  overall: number,
  thresholds?: Partial<BusinessHealthThresholds> | null
): string =>
  BIRDS_EYE_NODE_FILL[clusterFromOverall(overall, thresholds)] ??
  BIRDS_EYE_NODE_FILL_FALLBACK;

export type BirdsEyeQuadrant = "expand" | "triage" | "defend" | "delegate";

export const BIRDS_EYE_CLUSTER_META: Record<
  BirdsEyeMapClusterId,
  { label: string; color: string; bg: string; text: string }
> = {
  "renewal-risk": {
    label: "Renewal Risk",
    color: "#ef4444",
    bg: "#fee2e2",
    text: "#dc2626",
  },
  "strong-performers": {
    label: "Strong Performers",
    color: "#22c55e",
    bg: "#dcfce7",
    text: "#16a34a",
  },
  "high-adoption-mixed": {
    label: "High Adoption / Mixed",
    color: "#eab308",
    bg: "#fefce8",
    text: "rgb(209 160 7)",
  },
};

/** Risk–Opportunity plot geometry (Always On reach map). */
export const REACH_MAP_GEOM = {
  W: 470,
  H: 278,
  padL: 46,
  padR: 10,
  padT: 20,
  padB: 32,
} as const;

export const REACH_NODE_R = 8;
export const SELECTION_RING_GAP = 3;
export const REACH_COLLISION_BASE_PX = 12;

const clampRange = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

/**
 * Midpoint semantics: x = opportunity (≥0.5 high), y = risk (≥0.5 high).
 * Chart maps high risk to the top.
 */
export function quadrantFromReach(
  opp01: number,
  risk01: number
): BirdsEyeQuadrant {
  if (risk01 >= 0.5 && opp01 < 0.5) return "triage";
  if (risk01 >= 0.5 && opp01 >= 0.5) return "defend";
  if (risk01 < 0.5 && opp01 < 0.5) return "delegate";
  return "expand";
}

/** Small deterministic offset so accounts in the same band don’t share one pixel. */
function reachSpread01(accountId: string): { dx: number; dy: number } {
  let h = 0;
  for (let i = 0; i < accountId.length; i++) {
    h = (h * 31 + accountId.charCodeAt(i)) >>> 0;
  }
  const u1 = (h & 0xffff) / 0xffff;
  const u2 = ((h >>> 16) & 0xffff) / 0xffff;
  return { dx: (u1 - 0.5) * 0.08, dy: (u2 - 0.5) * 0.08 };
}

/**
 * Risk–Opportunity placement from Account Health score bands
 * (Settings vendor_config.health_score_thresholds.account_health).
 * Centers stay inside the target quadrant; jitter stays on the correct side of 0.5.
 */
export function overallScoreToReach01(
  overall: number,
  accountId: string,
  thresholds?: Partial<AccountHealthThresholds> | null
): { reachOpp: number; reachRisk: number } {
  const bands = normalizeAccountHealthThresholds(
    thresholds ?? DEFAULT_ACCOUNT_HEALTH_THRESHOLDS
  );
  const s = Math.round(clampOverallScore(overall));
  let opp: number;
  let risk: number;

  const triageSpan = Math.max(
    1,
    bands.defend_min - Math.max(1, bands.triage_min)
  );
  const defendSpan = Math.max(1, bands.delegate_min - bands.defend_min);
  const delegateSpan = Math.max(1, bands.expand_min - bands.delegate_min);
  const expandSpan = Math.max(1, 100 - bands.expand_min);

  if (s <= 0) {
    opp = 0.2;
    risk = 0.8;
  } else if (s < bands.defend_min) {
    // Triage: high risk, low opportunity
    const t = (Math.max(bands.triage_min, s) - bands.triage_min) / triageSpan;
    opp = 0.14 + t * 0.26;
    risk = 0.86 - t * 0.22;
  } else if (s < bands.delegate_min) {
    // Defend: high risk, high opportunity
    const t = (s - bands.defend_min) / defendSpan;
    opp = 0.6 + t * 0.26;
    risk = 0.86 - t * 0.22;
  } else if (s < bands.expand_min) {
    // Monitor: low risk, low opportunity
    const t = (s - bands.delegate_min) / delegateSpan;
    opp = 0.14 + t * 0.26;
    risk = 0.36 - t * 0.22;
  } else {
    // Expand: low risk, high opportunity
    const t = Math.min(1, (s - bands.expand_min) / expandSpan);
    opp = 0.6 + t * 0.28;
    risk = 0.36 - t * 0.24;
  }

  const sp = reachSpread01(accountId);
  let reachOpp = clampRange(opp + sp.dx, 0.04, 0.96);
  let reachRisk = clampRange(risk + sp.dy, 0.04, 0.96);

  // Keep jitter from crossing the quadrant midlines
  if (s <= 0) {
    reachOpp = Math.min(reachOpp, 0.48);
    reachRisk = Math.max(reachRisk, 0.52);
  } else if (s < bands.defend_min) {
    reachOpp = Math.min(reachOpp, 0.48);
    reachRisk = Math.max(reachRisk, 0.52);
  } else if (s < bands.delegate_min) {
    reachOpp = Math.max(reachOpp, 0.52);
    reachRisk = Math.max(reachRisk, 0.52);
  } else if (s < bands.expand_min) {
    reachOpp = Math.min(reachOpp, 0.48);
    reachRisk = Math.min(reachRisk, 0.48);
  } else {
    reachOpp = Math.max(reachOpp, 0.52);
    reachRisk = Math.min(reachRisk, 0.48);
  }

  return { reachOpp, reachRisk };
}

export type CollisionOffset = { dx: number; dy: number };

/**
 * Visual-only de-overlap: when multiple accounts render at same pixel,
 * fan them out deterministically while preserving true underlying values.
 */
export function buildCollisionOffsets<T extends { account_id: string }>(
  points: T[],
  getX: (p: T) => number,
  getY: (p: T) => number,
  baseRadiusPx: number
): Map<string, CollisionOffset> {
  const byPixel = new Map<string, T[]>();

  points.forEach(p => {
    const px = Math.round(getX(p));
    const py = Math.round(getY(p));
    const key = `${px}:${py}`;
    if (!byPixel.has(key)) byPixel.set(key, []);
    byPixel.get(key)!.push(p);
  });

  const offsets = new Map<string, CollisionOffset>();
  byPixel.forEach(group => {
    const sorted = [...group].sort((a, b) =>
      a.account_id.localeCompare(b.account_id)
    );
    const count = sorted.length;
    if (count === 1) {
      offsets.set(sorted[0].account_id, { dx: 0, dy: 0 });
      return;
    }

    sorted.forEach((p, idx) => {
      const angle = (2 * Math.PI * idx) / count;
      const ring = Math.floor(idx / 8);
      const radius = baseRadiusPx + ring * 6;
      offsets.set(p.account_id, {
        dx: Math.cos(angle) * radius,
        dy: Math.sin(angle) * radius,
      });
    });
  });

  return offsets;
}

export const BIRDS_EYE_QUADRANT_ORDER: BirdsEyeQuadrant[] = [
  "triage",
  "defend",
  "delegate",
  "expand",
];

/** Quadrant action cards — shared by Always On + My Accounts Bird's Eye. */
export const BIRDS_EYE_QUADRANT_ACTIONS: Record<
  BirdsEyeQuadrant,
  { border: string; titleColor: string; blurb: string; items: string[] }
> = {
  triage: {
    border: "#ef4444",
    titleColor: "#ef4444",
    blurb: "High risk, low opportunity",
    items: [
      "Assign exec sponsor immediately",
      "Emergency account review within 7 days",
      "Activate churn prevention playbook",
    ],
  },
  defend: {
    border: "#f97316",
    titleColor: "#f97316",
    blurb: "High risk, high opportunity",
    items: [
      "Prioritize exec-to-exec relationship",
      "Fast-track open escalations",
      "Lock renewal before competitor entry",
    ],
  },
  delegate: {
    border: "#94a3b8",
    titleColor: "#64748b",
    blurb: "Low risk, low opportunity",
    items: [
      "Automate health touchpoints",
      "Quarterly review cycle sufficient",
      "Monitor for expansion signals",
    ],
  },
  expand: {
    border: "#22c55e",
    titleColor: "#16a34a",
    blurb: "Low risk, high opportunity",
    items: [
      "Open expansion conversation now",
      "Nominate for reference / case study",
      "Leverage exec network introductions",
    ],
  },
};

/** Pill / badge colors for quadrant + cluster chips in hover cards. */
export const BIRDS_EYE_QUADRANT_PILL: Record<
  BirdsEyeQuadrant,
  { label: string; color: string; bg: string; text: string }
> = {
  triage: { label: "Triage", color: "#ef4444", bg: "#fee2e2", text: "#dc2626" },
  defend: { label: "Defend", color: "#f97316", bg: "#ffedd5", text: "#ea580c" },
  delegate: {
    label: "Monitor",
    color: "#94a3b8",
    bg: "#f1f5f9",
    text: "#64748b",
  },
  expand: { label: "Expand", color: "#22c55e", bg: "#dcfce7", text: "#16a34a" },
};

/** Always On quadrant strip metadata (tailwind + SVG fills). */
export const BIRDS_EYE_QUADRANT_STRIP: Record<
  BirdsEyeQuadrant,
  {
    label: string;
    color: string;
    text: string;
    fill: string;
    stripTitle: string;
    stripBlurb: string;
  }
> = {
  triage: {
    label: "Triage",
    color: "bg-rose-500",
    text: "text-rose-700",
    fill: "#ef4444",
    stripTitle: "Triage",
    stripBlurb: BIRDS_EYE_QUADRANT_ACTIONS.triage.blurb,
  },
  defend: {
    label: "Defend",
    color: "bg-orange-500",
    text: "text-orange-700",
    fill: "#f97316",
    stripTitle: "Defend",
    stripBlurb: BIRDS_EYE_QUADRANT_ACTIONS.defend.blurb,
  },
  delegate: {
    label: "Monitor",
    color: "bg-slate-400",
    text: "text-slate-700",
    fill: "#94a3b8",
    stripTitle: "Monitor",
    stripBlurb: BIRDS_EYE_QUADRANT_ACTIONS.delegate.blurb,
  },
  expand: {
    label: "Expand",
    color: "bg-emerald-500",
    text: "text-emerald-700",
    fill: "#16a34a",
    stripTitle: "Expand",
    stripBlurb: BIRDS_EYE_QUADRANT_ACTIONS.expand.blurb,
  },
};

export function birdsEyeInitials(name: string): string {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .map(w => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function reachPlotX(
  reachOpp: number,
  plotW: number,
  padL: number
): number {
  return padL + (reachOpp * 0.88 + 0.06) * plotW;
}

export function reachPlotY(
  reachRisk: number,
  plotH: number,
  padT: number
): number {
  return padT + (1 - (reachRisk * 0.88 + 0.06)) * plotH;
}

export function formatReachAxis01(value: number): string {
  return Number.isFinite(value) ? `${Math.round(value * 100)}%` : "—";
}

export function clusterInsightLine(cluster: BirdsEyeMapClusterId): string {
  switch (cluster) {
    case "renewal-risk":
      return "Renewal exposure elevated — align exec sponsor and renewal playbook.";
    case "strong-performers":
      return "Strong trajectory — prioritize expansion and advocacy.";
    default:
      return "Mixed signals — monitor adoption and executive engagement.";
  }
}

export function computeReachMapQuadrantLabels(plotH: number, padT: number) {
  const qLabelInset = 2;
  const qTitleSubGap = 3;
  const qTitleToSub = 8 + qTitleSubGap;
  const qTopTitleY = padT + 13 - qLabelInset;
  const qTopSubY = qTopTitleY + qTitleToSub;
  const qBotSubY = padT + plotH - 5 - qLabelInset;
  const qBotTitleY = qBotSubY - qTitleToSub;
  return { qTopTitleY, qTopSubY, qBotTitleY, qBotSubY };
}
